<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { ref } from "vue"

import FormActions from "@/components/FormActions.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import ScannerModal from "@/components/ScannerModal.vue"
import { barcodeScannerSupported } from "@/composables/useBarcodeScanner"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import { itemPath } from "@/routes"
import type { EditItem, SelectOption } from "@/types/models"

const props = defineProps<{
  item: EditItem
  containers: SelectOption[]
  item_types: SelectOption[]
  people: SelectOption[]
}>()

const { title } = useNavbar({ title: `Edit ${props.item.name}` })

const name = ref(props.item.name)
const description = ref(props.item.description ?? "")
const notes = ref(props.item.notes ?? "")
const condition = ref(props.item.condition ?? "")
const quantity = ref(props.item.quantity)
const consumable = ref(props.item.consumable)
const parentId = ref<string | undefined>(props.item.parent_id ?? undefined)
const itemTypeId = ref<string | undefined>(props.item.item_type_id ?? undefined)
const personId = ref<string | undefined>(props.item.person_id ?? undefined)
const assetCode = ref(props.item.asset_code ?? "")
const barcode = ref(props.item.barcode ?? "")
const modelNumber = ref(props.item.model_number ?? "")
const serialNumber = ref(props.item.serial_number ?? "")

const saving = ref(false)
const formErrors = ref<Record<string, string>>({})
const scannerOpen = ref(false)
const scanTarget = ref<"assetCode" | "barcode" | null>(null)
const scannerSupported = barcodeScannerSupported

function submit() {
  saving.value = true
  formErrors.value = {}

  router.patch(
    itemPath({ id: props.item.id }),
    {
      item: {
        name: name.value,
        description: description.value || null,
        notes: notes.value || null,
        condition: condition.value || null,
        quantity: quantity.value,
        consumable: consumable.value,
        parent_id: parentId.value || null,
        item_type_id: itemTypeId.value || null,
        person_id: personId.value || null,
        asset_code: assetCode.value || null,
        barcode: barcode.value || null,
        model_number: modelNumber.value || null,
        serial_number: serialNumber.value || null,
      },
    },
    {
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    },
  )
}

function cancel() {
  router.visit(itemPath({ id: props.item.id }))
}

function openScanner(target: "assetCode" | "barcode") {
  scanTarget.value = target
  scannerOpen.value = true
}

function handleFieldScan(code: string) {
  if (scanTarget.value === "assetCode") {
    assetCode.value = code
  } else if (scanTarget.value === "barcode") {
    barcode.value = code
  }
  scannerOpen.value = false
  scanTarget.value = null
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <h1 class="text-highlighted text-xl font-semibold">Edit {{ item.name }}</h1>

    <form class="flex flex-col gap-6" @submit.prevent="submit">
      <UFormField label="Name" required :error="formErrors.name">
        <UInput v-model="name" autofocus class="w-full" />
      </UFormField>

      <UFormField label="Description" :error="formErrors.description">
        <UTextarea v-model="description" :rows="3" class="w-full" />
      </UFormField>

      <UFormField label="Notes" :error="formErrors.notes">
        <UTextarea v-model="notes" :rows="3" class="w-full" />
      </UFormField>

      <UFormField label="Condition" :error="formErrors.condition">
        <UInput v-model="condition" class="w-full" />
      </UFormField>

      <div class="flex items-end gap-4">
        <UFormField label="Quantity" :error="formErrors.quantity" class="w-32">
          <UInputNumber v-model="quantity" :min="1" class="w-full" />
        </UFormField>

        <UFormField>
          <div class="flex items-center gap-2">
            <USwitch v-model="consumable" />
            <span class="text-sm">Consumable</span>
          </div>
        </UFormField>
      </div>

      <USeparator />

      <UFormField label="Container" :error="formErrors.parent_id">
        <USelectMenu
          v-model="parentId"
          :items="containers"
          value-key="value"
          placeholder="None"
          class="w-full"
        />
      </UFormField>

      <UFormField label="Type" :error="formErrors.item_type_id">
        <USelectMenu
          v-model="itemTypeId"
          :items="item_types"
          value-key="value"
          placeholder="None"
          class="w-full"
        />
      </UFormField>

      <UFormField label="Owner" :error="formErrors.person_id">
        <USelectMenu
          v-model="personId"
          :items="people"
          value-key="value"
          placeholder="None"
          class="w-full"
        />
      </UFormField>

      <USeparator />

      <UFormField label="Asset Code" :error="formErrors.asset_code">
        <UInput v-model="assetCode" class="w-full font-mono">
          <template v-if="scannerSupported" #trailing>
            <UButton
              :icon="ICON.scanQr"
              color="neutral"
              variant="link"
              size="xs"
              :padded="false"
              @click="openScanner('assetCode')"
            />
          </template>
        </UInput>
      </UFormField>

      <UFormField label="Barcode" :error="formErrors.barcode">
        <UInput v-model="barcode" class="w-full font-mono">
          <template v-if="scannerSupported" #trailing>
            <UButton
              :icon="ICON.scanQr"
              color="neutral"
              variant="link"
              size="xs"
              :padded="false"
              @click="openScanner('barcode')"
            />
          </template>
        </UInput>
      </UFormField>

      <UFormField label="Model Number" :error="formErrors.model_number">
        <UInput v-model="modelNumber" class="w-full font-mono" />
      </UFormField>

      <UFormField label="Serial Number" :error="formErrors.serial_number">
        <UInput v-model="serialNumber" class="w-full font-mono" />
      </UFormField>

      <FormActions :loading="saving" @cancel="cancel" />
    </form>
  </NarrowPage>

  <ScannerModal v-model:open="scannerOpen" @scan="handleFieldScan" />
</template>
